import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { getSharedDocuments } from '../redux/slices/document';
import {
	Box,
	Card,
	CardContent,
	Container,
	Alert,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography
} from '@mui/material';
import { CloudDownloadTwoTone, GppMaybeOutlined, OpenInBrowserTwoTone, VerifiedOutlined } from '@mui/icons-material';
import { SharedRecordFile } from '../types/document';

import SharedFileVerification from '../components/ui/molecules/SharedFileVerification';
import Logo from '../components/layout/Logo';
import { downloadUrl } from '../utils';
import {
	SHARED_DOCUMENTS_CARD_CLASS,
	SHARED_DOCUMENTS_ITEM_CLASS,
	SHARED_DOCUMENTS_STATUS_ICON_CLASS,
	SHARED_DOCUMENTS_UNVERIFIED_ICON_CLASS
} from '../themes/sharedDocumentsStyles';

const Sharing = () => {
	const { sharingId } = useParams<{ sharingId: string }>();
	const [searchParams] = useSearchParams();
	const recipientToken = searchParams.get('r') ?? undefined;
	const sharedRecord = useAppSelector((state) => state.document.sharedRecord);
	const dispatch = useAppDispatch();
	const { t } = useTranslation();
	const [withdrawn, setWithdrawn] = useState(false);

	useEffect(() => {
		if (sharingId) {
			setWithdrawn(false);
			dispatch(getSharedDocuments({ sharingId, token: recipientToken }))
				.unwrap()
				.catch((error) => {
					// 410: the sender's organisation withdrew the document, or restricted sharing it.
					if (error?.status === 410) setWithdrawn(true);
				});
		}
	}, [dispatch, sharingId, recipientToken]);

	const openDocument = (url: string | undefined) => {
		if (url) {
			window.open(url, '_blank');
		} else {
			console.error('Document URL is undefined.');
		}
	};

	const downloadDocument = (url: string | undefined, fileName: string) => {
		if (url) {
			downloadUrl(url, fileName);
		} else {
			console.error('Document URL is undefined.');
		}
	};

	return (
		<Container>
			<Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				<Card className={SHARED_DOCUMENTS_CARD_CLASS}>
					<CardContent sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
						<Box display={'flex'} justifyContent={'center'}>
							<Logo />
						</Box>
						<Typography variant="h6">{t('pages.sharing.recipientView.title')}</Typography>
						<Divider />
						{withdrawn && <Alert severity="info">{t('pages.sharing.recipientView.withdrawn')}</Alert>}
						<List>
							{sharedRecord?.files?.map((doc: SharedRecordFile) => (
								<ListItem key={doc.id} className={SHARED_DOCUMENTS_ITEM_CLASS}>
									<ListItemIcon className={SHARED_DOCUMENTS_STATUS_ICON_CLASS}>
										{doc.verification?.verified ? (
											<VerifiedOutlined color="success" />
										) : (
											<GppMaybeOutlined className={SHARED_DOCUMENTS_UNVERIFIED_ICON_CLASS} />
										)}
									</ListItemIcon>
									<ListItemText
										primary={doc.title}
										secondary={<SharedFileVerification verification={doc.verification} />}
										secondaryTypographyProps={{ component: 'div' }}
									/>
									<IconButton onClick={() => openDocument(doc.downloadURL)}>
										<OpenInBrowserTwoTone />
									</IconButton>
									<IconButton onClick={() => downloadDocument(doc.downloadURL, doc.title)}>
										<CloudDownloadTwoTone />
									</IconButton>
								</ListItem>
							))}
						</List>
					</CardContent>
				</Card>
			</Box>
		</Container>
	);
};

export default Sharing;
